/**
 * Pure text builders for the streaming CSV export progress toast.
 *
 * Long exports (millions of rows) live in this toast for many minutes, so the
 * copy has three jobs: show movement (rows + percent), promise an end (ETA
 * once the rate is stable), and never look dead — a stalled or retrying batch
 * gets an explicit "still working" line instead of a frozen row count.
 */

export type ExportProgressSnapshot = {
  current: number;
  total: number;
  /** Milliseconds since the export started. */
  elapsedMs: number;
  /**
   * S2 — cumulative number of unreadable rows skipped so far. Rows the export
   * silently leaves out are the one thing a progress toast must not hide, so
   * the count rides along with every progress line once it goes above zero.
   */
  skipped?: number;
};

/** Don't show an ETA before the rate has had a moment to stabilize. */
const MIN_ELAPSED_FOR_ETA_MS = 5_000;

/**
 * Human ETA from the average rate so far, or `null` when there isn't enough
 * signal yet (too early, nothing written, or unknown total).
 */
export function formatExportRemaining(snapshot: ExportProgressSnapshot): string | null {
  const { current, total, elapsedMs } = snapshot;
  if (elapsedMs < MIN_ELAPSED_FOR_ETA_MS || current <= 0 || total <= 0 || current >= total) {
    return null;
  }
  const remainingMs = ((total - current) * elapsedMs) / current;
  const remainingSec = Math.ceil(remainingMs / 1000);
  if (remainingSec < 90) {
    // Round up to 5s steps — a jittery "13s, 11s, 14s" countdown reads worse
    // than a steady "15s".
    return `about ${Math.max(5, Math.ceil(remainingSec / 5) * 5)}s left`;
  }
  const remainingMin = Math.round(remainingSec / 60);
  if (remainingMin < 90) return `about ${remainingMin} min left`;
  const hours = Math.floor(remainingMin / 60);
  const minutes = remainingMin % 60;
  return minutes > 0 ? `about ${hours}h ${minutes}m left` : `about ${hours}h left`;
}

/**
 * " · 3 rows skipped" — appended to every progress line while the export is
 * skipping unreadable rows, and empty until the first skip so the common case
 * reads exactly as before.
 */
export function formatSkippedSuffix(skipped: number | undefined): string {
  if (!skipped || skipped <= 0) return '';
  return ` · ${skipped.toLocaleString()} ${skipped === 1 ? 'row' : 'rows'} skipped`;
}

function rowsAndPercent(snapshot: ExportProgressSnapshot): string {
  const pct = snapshot.total > 0 ? Math.round((snapshot.current / snapshot.total) * 100) : 0;
  return `${snapshot.current.toLocaleString()} / ${snapshot.total.toLocaleString()} rows (${pct}%)${formatSkippedSuffix(snapshot.skipped)}`;
}

/**
 * Normal progress text: "1,816,000 / 3,494,925 rows (52%)" with the ETA on
 * its own line ("about 4 min left"). The newline (rendered via the toast
 * description's `whitespace-pre-line`) keeps the wrap point fixed — letting
 * the browser wrap mid-sentence put "— about" on the first line and "15s
 * left" on the second, shifting as the numbers grew.
 */
export function describeExportProgress(snapshot: ExportProgressSnapshot): string {
  const eta = formatExportRemaining(snapshot);
  return eta ? `${rowsAndPercent(snapshot)}\n${eta}` : rowsAndPercent(snapshot);
}

/** Shown when no progress event has arrived for a while — the export is NOT dead. */
export function describeExportStall(snapshot: ExportProgressSnapshot): string {
  return `Still working — the server is taking a while at ${rowsAndPercent(snapshot)}`;
}

/** Shown while a stalled batch is being retried (the export keeps going). */
export function describeExportRetry(
  snapshot: ExportProgressSnapshot,
  retry: { attempt: number; maxAttempts: number },
): string {
  return `Server slow to respond — retrying (attempt ${retry.attempt + 1} of ${retry.maxAttempts}) at ${rowsAndPercent(snapshot)}`;
}

/**
 * Shown while a stalled batch is being bisected into smaller fetches (the
 * export keeps going — this is the recovery mode for windows the server can't
 * return at full batch size, e.g. rows with huge cells).
 */
export function describeExportRecovery(
  snapshot: ExportProgressSnapshot,
  recovery: { batchSize: number },
): string {
  const unit = recovery.batchSize === 1 ? 'row' : 'rows';
  return `Working through a difficult spot — fetching ${recovery.batchSize} ${unit} at a time at ${rowsAndPercent(snapshot)}`;
}
